import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '@/lib/i18n';
import { api } from '@/lib/api';
import { useCurrency } from '@/Hooks/useCurrency';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Field, Select } from '@/components/ui/field';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { TD, TH, THead, TR, Table, TableWrap } from '@/components/ui/table';
import {
  RiskEventStatus,
  RiskRuleKey,
  RiskSeverity,
  apiError,
  riskSeverityTone,
  riskStatusTone,
  useAutoRefresh,
  type QueryRiskEventsParams,
  type RiskEvent,
} from './lib';
import { useStaffNames } from './useStaffNames';
import CaseDetailDialog from './CaseDetailDialog';

const POLL_MS = 60_000;

/**
 * The case queue. Every row is an allegation about a named person or, for the
 * two rules that measure a branch instead, about nobody in particular — the
 * subject column says which. `risk.review` is the route's own gate; this tab
 * reads nothing more sensitive than what that key already exposes.
 */
export default function QueueTab() {
  const { t, i18n } = useTranslation();
  const { nameOf } = useStaffNames();

  const [status, setStatus] = useState<RiskEventStatus | ''>(RiskEventStatus.OPEN);
  const [severity, setSeverity] = useState<RiskSeverity | ''>('');
  const [rule, setRule] = useState<RiskRuleKey | ''>('');

  const [rows, setRows] = useState<RiskEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    const params: QueryRiskEventsParams = {
      limit: 100,
      status: status || undefined,
      severity: severity || undefined,
      rule: rule || undefined,
    };
    api
      .get<{ data: RiskEvent[] }>('/risk/events', { params })
      .then(({ data }) => setRows(data.data ?? []))
      .catch((e) => setError(apiError(e, t('risk.queue.loadFailed'))))
      .finally(() => setLoading(false));
  }, [status, severity, rule, t]);

  useEffect(load, [load]);
  useAutoRefresh(load, { enabled: true, intervalMs: POLL_MS });

  return (
    <div className="space-y-3">
      <Card>
        <CardContent className="flex flex-wrap gap-3 p-4">
          <Field label={t('risk.queue.status')}>
            <Select value={status} onChange={(e) => setStatus(e.target.value as RiskEventStatus | '')}>
              <option value="">{t('risk.queue.anyStatus')}</option>
              {Object.values(RiskEventStatus).map((s) => (
                <option key={s} value={s}>
                  {t(`risk.status.${s}`)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('risk.queue.severity')}>
            <Select value={severity} onChange={(e) => setSeverity(e.target.value as RiskSeverity | '')}>
              <option value="">{t('risk.queue.anySeverity')}</option>
              {Object.values(RiskSeverity).map((s) => (
                <option key={s} value={s}>
                  {t(`risk.severity.${s}`)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('risk.queue.rule')}>
            <Select value={rule} onChange={(e) => setRule(e.target.value as RiskRuleKey | '')}>
              <option value="">{t('risk.queue.anyRule')}</option>
              {Object.values(RiskRuleKey).map((r) => (
                <option key={r} value={r}>
                  {t(`risk.rule.${r}`)}
                </option>
              ))}
            </Select>
          </Field>
        </CardContent>
      </Card>

      {loading && rows.length === 0 ? (
        <LoadingState label={t('common.loading')} />
      ) : error ? (
        <ErrorState message={error} onRetry={load} />
      ) : rows.length === 0 ? (
        <EmptyState
          title={t(status === RiskEventStatus.OPEN ? 'risk.queue.emptyOpen' : 'risk.queue.empty')}
          hint={t('risk.queue.emptyHint')}
        />
      ) : (
        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH>{t('risk.queue.rule')}</TH>
                <TH>{t('risk.case.subject')}</TH>
                <TH>{t('risk.queue.severity')}</TH>
                <TH>{t('risk.queue.status')}</TH>
                <TH>{t('risk.queue.lastObserved')}</TH>
              </TR>
            </THead>
            <tbody>
              {rows.map((row) => (
                <QueueRow key={row.id} row={row} subject={nameOf(row.subjectUserId)} locale={i18n.language} onOpen={() => setOpenId(row.id)} />
              ))}
            </tbody>
          </Table>
        </TableWrap>
      )}

      {openId && (
        <CaseDetailDialog
          eventId={openId}
          onClose={() => setOpenId(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}

function QueueRow({
  row,
  subject,
  locale,
  onOpen,
}: {
  row: RiskEvent;
  subject: string | null;
  locale: string;
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  const { format } = useCurrency(row.branchId);
  return (
    <TR className="cursor-pointer hover:bg-hover" onClick={onOpen}>
      <TD className="font-medium">{t(`risk.rule.${row.rule}`)}</TD>
      <TD>{subject ?? <span className="text-ink-muted">{t('risk.case.noSubject')}</span>}</TD>
      <TD>
        <Badge tone={riskSeverityTone(row.severity)}>{t(`risk.severity.${row.severity}`)}</Badge>
      </TD>
      <TD>
        <Badge tone={riskStatusTone(row.status)}>{t(`risk.status.${row.status}`)}</Badge>
      </TD>
      <TD className="tabular-nums">
        {t('risk.queue.observedCell', { count: row.observedCount, amount: format(row.observedAmount) })}
      </TD>
      <TD className="text-ink-muted">{formatDateTime(row.lastObservedAt, locale)}</TD>
    </TR>
  );
}
